import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { BomNode, BomQueryOptions, AddBomChildRequest, UpdateBomChildRequest, PartRelationship, Part } from '../../models/file-vault.models';
import { LifecycleBadgeComponent } from '../lifecycle-badge/lifecycle-badge.component';

export interface BomChangeEvent {
  action: 'add' | 'update' | 'remove' | 'reorder';
  relationship?: PartRelationship;
  partId: string;
}

@Component({
  selector: 'fv-bom-editor',
  standalone: true,
  template: `
    <div class="bom-editor-container">
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="toolbar-left">
          <span class="title" *ngIf="rootPartNumber">Editing BOM: {{ rootPartNumber }}</span>
        </div>
        <div class="toolbar-right">
          <button kendoButton [themeColor]="'primary'" (click)="openAddDialog()" [disabled]="!canEdit">
            Add Item
          </button>
          <button kendoButton [look]="'flat'" (click)="refresh()">
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">Loading BOM...</div>

      <!-- BOM Grid -->
      <kendo-grid
        *ngIf="!isLoading"
        [data]="gridData"
        [selectable]="{ mode: 'single' }"
        [sortable]="true"
        [reorderable]="canEdit"
        (selectionChange)="onSelectionChange($event)"
        class="bom-grid">

        <kendo-grid-column field="sortOrder" title="#" [width]="50">
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ rowIndex + 1 }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="findNumber" title="Find #" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.findNumber || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partNumber" title="Part Number" [width]="160">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="part-number">{{ dataItem.part?.partNumber || '-' }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partName" title="Name" [width]="200">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.part?.name || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="quantity" title="Qty" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="quantity-cell" *ngIf="canEdit">
              <input type="number"
                     class="qty-input"
                     [value]="dataItem.quantity"
                     (change)="onQuantityChange(dataItem, $event)"
                     min="0"
                     step="1" />
              <span class="uom">{{ dataItem.relationship?.unitOfMeasure || dataItem.part?.unitOfMeasure || 'EA' }}</span>
            </div>
            <span *ngIf="!canEdit">
              {{ dataItem.quantity }} {{ dataItem.relationship?.unitOfMeasure || 'EA' }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="refDesignator" title="Ref Des" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.refDesignator || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="lifecycleState" title="State" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <fv-lifecycle-badge
              [state]="dataItem.part?.lifecycleState"
              [showIcon]="false">
            </fv-lifecycle-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Actions" [width]="100" *ngIf="canEdit">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="action-buttons">
              <button kendoButton fillMode="flat" (click)="openEditDialog(dataItem)" title="Edit" aria-label="Edit">
                <kendo-icon name="pencil"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat" (click)="confirmRemove(dataItem)" title="Remove" aria-label="Remove"
                      class="btn-danger">
                <kendo-icon name="trash"></kendo-icon>
              </button>
            </div>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Add/Edit Dialog -->
      <kendo-dialog *ngIf="showDialog"
                    [title]="dialogTitle"
                    [width]="500"
                    (close)="closeDialog()">
        <div class="dialog-content">
          <!-- Part Selection (Add mode only) -->
          <div class="form-group" *ngIf="dialogMode === 'add'">
            <label>Part Number *</label>
            <input kendoTextBox
                   [(ngModel)]="editingItem.partNumber"
                   placeholder="Enter part number to search..."
                   (input)="onPartSearch($event)" />
            <div class="part-search-results" *ngIf="searchResults.length > 0">
              <div class="search-result"
                   *ngFor="let part of searchResults"
                   (click)="selectPart(part)">
                <span class="result-number">{{ part.partNumber }}</span>
                <span class="result-name">{{ part.name }}</span>
              </div>
            </div>
            <div class="selected-part" *ngIf="editingItem.selectedPart">
              Selected: {{ editingItem.selectedPart.partNumber }} - {{ editingItem.selectedPart.name }}
            </div>
          </div>

          <!-- Quantity -->
          <div class="form-group">
            <label>Quantity *</label>
            <kendo-numerictextbox
              [(ngModel)]="editingItem.quantity"
              [min]="0.001"
              [step]="1"
              [decimals]="3"
              [format]="'n3'">
            </kendo-numerictextbox>
          </div>

          <!-- Unit of Measure -->
          <div class="form-group">
            <label>Unit of Measure</label>
            <kendo-dropdownlist
              [(ngModel)]="editingItem.unitOfMeasure"
              [data]="uomOptions"
              [valuePrimitive]="true">
            </kendo-dropdownlist>
          </div>

          <!-- Find Number -->
          <div class="form-group">
            <label>Find Number</label>
            <input kendoTextBox [(ngModel)]="editingItem.findNumber" placeholder="e.g., 1, 2, 3..." />
          </div>

          <!-- Reference Designator -->
          <div class="form-group">
            <label>Reference Designator</label>
            <input kendoTextBox [(ngModel)]="editingItem.refDesignator" placeholder="e.g., R1, C2, U3..." />
          </div>

          <!-- Notes -->
          <div class="form-group">
            <label>Notes</label>
            <textarea kendoTextArea [(ngModel)]="editingItem.notes" [rows]="2"></textarea>
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialog()">Cancel</button>
          <button kendoButton
                  [themeColor]="'primary'"
                  (click)="saveItem()"
                  [disabled]="!canSave">
            {{ dialogMode === 'add' ? 'Add' : 'Save' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Remove Confirmation Dialog -->
      <kendo-dialog *ngIf="showRemoveConfirm"
                    [title]="'Remove Item'"
                    [width]="400"
                    (close)="showRemoveConfirm = false">
        <p>Are you sure you want to remove <strong>{{ itemToRemove?.part?.partNumber }}</strong> from the BOM?</p>
        <kendo-dialog-actions>
          <button kendoButton (click)="showRemoveConfirm = false">Cancel</button>
          <button kendoButton [themeColor]="'error'" (click)="removeItem()">Remove</button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .bom-editor-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
    }

    .toolbar-left, .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .title {
      font-weight: 600;
      font-size: 14px;
    }

    .loading {
      padding: 24px;
      text-align: center;
      color: var(--fv-text-secondary);
    }

    .bom-grid {
      flex: 1;
      font-size: 13px;
    }

    .part-number {
      font-family: monospace;
      font-weight: 500;
    }

    .quantity-cell {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .qty-input {
      width: 60px;
      padding: 4px 8px;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      font-size: 13px;
    }

    .uom {
      font-size: 11px;
      color: var(--fv-text-secondary);
    }

    .action-buttons {
      display: flex;
      gap: 4px;
    }

    .btn-danger { color: #dc3545; }

    .dialog-content {
      padding: 16px 0;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-group label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
      font-size: 13px;
    }

    .form-group input,
    .form-group textarea,
    .form-group kendo-dropdownlist,
    .form-group kendo-numerictextbox {
      width: 100%;
    }

    .part-search-results {
      max-height: 150px;
      overflow-y: auto;
      border: 1px solid var(--fv-border);
      border-top: none;
      background: var(--fv-surface);
    }

    .search-result {
      padding: 8px 12px;
      cursor: pointer;
      display: flex;
      gap: 8px;
    }

    .search-result:hover {
      background: var(--fv-surface-hover);
    }

    .result-number {
      font-family: monospace;
      font-weight: 500;
    }

    .result-name {
      color: var(--fv-text-secondary);
    }

    .selected-part {
      margin-top: 8px;
      padding: 8px;
      background: var(--fv-status-success-bg);
      border-radius: 4px;
      font-size: 13px;
    }
  `],
  imports: [
    CommonModule, FormsModule, GridModule, ButtonsModule, DialogModule,
    InputsModule, DropDownsModule, IconsModule, KENDO_ICONS, LifecycleBadgeComponent
  ]
})
export class BomEditorComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Part ID to edit BOM for */
  @Input() partId?: string;

  /** Part number to edit BOM for (resolved to Part ID) */
  @Input() partNumber?: string;

  /** Part version ID (for version-specific BOM) */
  @Input() versionId?: string;

  /** Root part number for display */
  @Input() rootPartNumber?: string;

  /** Whether editing is allowed */
  @Input() canEdit = true;

  /** Emitted when BOM is changed */
  @Output() bomChanged = new EventEmitter<BomChangeEvent>();

  bomItems: BomNode[] = [];
  isLoading = false;
  private resolvedPartId?: string;

  // Dialog state
  showDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  editingItem: EditingBomItem = this.createEmptyEditingItem();
  editingRelationshipId?: string;

  // Part search
  searchResults: Part[] = [];
  private searchTimeout?: ReturnType<typeof setTimeout>;

  // Remove confirmation
  showRemoveConfirm = false;
  itemToRemove?: BomNode;

  uomOptions = ['EA', 'KG', 'LB', 'M', 'FT', 'IN', 'MM', 'CM', 'L', 'GAL', 'SET', 'PK'];

  get gridData(): GridDataResult {
    return {
      data: this.bomItems,
      total: this.bomItems.length
    };
  }

  get dialogTitle(): string {
    return this.dialogMode === 'add' ? 'Add BOM Item' : 'Edit BOM Item';
  }

  get canSave(): boolean {
    if (this.dialogMode === 'add') {
      return !!this.editingItem.selectedPart && this.editingItem.quantity > 0;
    }
    return this.editingItem.quantity > 0;
  }

  ngOnInit(): void {
    this.loadBom();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['partId'] || changes['partNumber'] || changes['versionId']) && !changes['partId']?.firstChange) {
      this.loadBom();
    }
  }

  async loadBom(): Promise<void> {
    if (!this.partId && !this.partNumber && !this.versionId) {
      this.bomItems = [];
      return;
    }

    this.isLoading = true;
    try {
      const options: BomQueryOptions = { maxDepth: 1 };

      let nodes: BomNode[];
      if (this.versionId) {
        nodes = await this.api.getBomForVersion(this.versionId, options).toPromise() ?? [];
      } else {
        const currentPartId = await this.getCurrentPartId();
        if (!currentPartId) {
          this.bomItems = [];
          return;
        }
        nodes = await this.api.getBom(currentPartId, options).toPromise() ?? [];
      }

      this.bomItems = nodes;
    } catch (error) {
      console.error('Failed to load BOM', error);
      this.bomItems = [];
    } finally {
      this.isLoading = false;
    }
  }

  onSelectionChange(event: any): void {
    // Handle selection if needed
  }

  async onQuantityChange(item: BomNode, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const quantity = parseFloat(input.value);

    if (isNaN(quantity) || quantity <= 0 || !item.relationship?.id) return;

    try {
      await this.api.updateBomQuantity(item.relationship.id, quantity).toPromise();
      item.quantity = quantity;
      this.bomChanged.emit({
        action: 'update',
        relationship: item.relationship,
        partId: this.partId || this.resolvedPartId || ''
      });
    } catch (error) {
      console.error('Failed to update quantity', error);
      input.value = item.quantity.toString();
    }
  }

  openAddDialog(): void {
    this.dialogMode = 'add';
    this.editingItem = this.createEmptyEditingItem();
    this.searchResults = [];
    this.showDialog = true;
  }

  openEditDialog(item: BomNode): void {
    this.dialogMode = 'edit';
    this.editingRelationshipId = item.relationship?.id;
    this.editingItem = {
      partNumber: item.part?.partNumber || '',
      selectedPart: item.part,
      quantity: item.quantity,
      unitOfMeasure: item.relationship?.unitOfMeasure || 'EA',
      findNumber: item.findNumber || '',
      refDesignator: item.refDesignator || '',
      notes: item.relationship?.notes || ''
    };
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.editingItem = this.createEmptyEditingItem();
    this.editingRelationshipId = undefined;
    this.searchResults = [];
  }

  onPartSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (!value || value.length < 2) {
      this.searchResults = [];
      return;
    }

    // Debounce search
    this.searchTimeout = setTimeout(async () => {
      // Note: This would need a parts search API endpoint
      // For now, we'll leave this as a placeholder
      // In real implementation, call: this.api.searchParts({ partNumber: value })
      this.searchResults = [];
    }, 300);
  }

  selectPart(part: Part): void {
    this.editingItem.selectedPart = part;
    this.editingItem.partNumber = part.partNumber;
    this.editingItem.unitOfMeasure = part.unitOfMeasure || 'EA';
    this.searchResults = [];
  }

  async saveItem(): Promise<void> {
    if (!this.canSave) return;

    try {
      if (this.dialogMode === 'add') {
        const request: AddBomChildRequest = {
          childPartId: this.editingItem.selectedPart!.id,
          quantity: this.editingItem.quantity,
          unitOfMeasure: this.editingItem.unitOfMeasure,
          findNumber: this.editingItem.findNumber || undefined,
          refDesignator: this.editingItem.refDesignator || undefined,
          notes: this.editingItem.notes || undefined
        };

        const currentPartId = await this.getCurrentPartId();
        if (!currentPartId) return;
        const relationship = await this.api.addBomChild(currentPartId, request).toPromise();
        this.bomChanged.emit({ action: 'add', relationship, partId: currentPartId });
      } else if (this.editingRelationshipId) {
        const request: UpdateBomChildRequest = {
          quantity: this.editingItem.quantity,
          unitOfMeasure: this.editingItem.unitOfMeasure,
          findNumber: this.editingItem.findNumber || undefined,
          refDesignator: this.editingItem.refDesignator || undefined,
          notes: this.editingItem.notes || undefined
        };

        const relationship = await this.api.updateBomChild(this.editingRelationshipId, request).toPromise();
        this.bomChanged.emit({ action: 'update', relationship, partId: this.partId || this.resolvedPartId || '' });
      }

      this.closeDialog();
      await this.loadBom();
    } catch (error) {
      console.error('Failed to save BOM item', error);
    }
  }

  confirmRemove(item: BomNode): void {
    this.itemToRemove = item;
    this.showRemoveConfirm = true;
  }

  async removeItem(): Promise<void> {
    if (!this.itemToRemove?.relationship?.id) return;

    try {
      await this.api.removeBomChild(this.itemToRemove.relationship.id).toPromise();
      this.bomChanged.emit({
        action: 'remove',
        relationship: this.itemToRemove.relationship,
        partId: this.partId || this.resolvedPartId || ''
      });
      await this.loadBom();
    } catch (error) {
      console.error('Failed to remove BOM item', error);
    } finally {
      this.showRemoveConfirm = false;
      this.itemToRemove = undefined;
    }
  }

  refresh(): void {
    this.loadBom();
  }

  private async getCurrentPartId(): Promise<string | undefined> {
    if (this.partId) {
      this.resolvedPartId = this.partId;
      return this.partId;
    }

    if (this.resolvedPartId && this.partNumber === undefined) {
      return this.resolvedPartId;
    }

    if (!this.partNumber) return undefined;

    try {
      const part = await this.api.getPartByNumber(this.partNumber).toPromise();
      if (part?.id) {
        this.resolvedPartId = part.id;
        if (!this.rootPartNumber) {
          this.rootPartNumber = part.partNumber;
        }
        return part.id;
      }
    } catch (error) {
      console.error('Failed to resolve part number', error);
    }

    return undefined;
  }

  private createEmptyEditingItem(): EditingBomItem {
    return {
      partNumber: '',
      selectedPart: undefined,
      quantity: 1,
      unitOfMeasure: 'EA',
      findNumber: '',
      refDesignator: '',
      notes: ''
    };
  }
}

interface EditingBomItem {
  partNumber: string;
  selectedPart?: Part;
  quantity: number;
  unitOfMeasure: string;
  findNumber: string;
  refDesignator: string;
  notes: string;
}
